/**
 * Mapeo y presentación de etiquetas claras de resolución y modalidades (Mejora 3 y 4).
 *
 * Mantiene compatibilidad técnica absoluta con los payloads de APIMart y los modelos
 * (sin sustituir identificadores internos 'std', 'pro', '720P', etc.).
 */

/**
 * Detecta si un modelo pertenece a la familia Kling
 */
export function isKlingModel(modelOrId) {
  const id = typeof modelOrId === "string" ? modelOrId : (modelOrId?.id || modelOrId?.model_id || "");
  return id === "kling-v3" || id.startsWith("kling-");
}

/**
 * Detecta si una resolución seleccionada corresponde al modo PRO de Kling
 */
export function isKlingPro(res, modelOrId) {
  if (!isKlingModel(modelOrId)) return false;
  const val = String(res || "").toLowerCase();
  return val === "pro" || val === "1080p";
}

/**
 * Devuelve la etiqueta visual comprensible para el usuario,
 * priorizando la resolución en píxeles y manteniendo la modalidad complementaria.
 *
 * @param {string} res Valor interno de resolución (ej. "std", "pro", "720P", "1K", etc.)
 * @param {object|string} model Modelo seleccionado o su ID
 * @returns {string} Etiqueta visual clara (ej. "720p — Standard (STD)")
 */
export function formatResolutionLabel(res, model) {
  if (!res) return "";
  const raw = String(res).trim();
  const lower = raw.toLowerCase();

  // 1. Familia Kling (Kling 3.0, Kling 2.6, Kling Motion Control, Kling Turbo, etc.)
  if (isKlingModel(model)) {
    if (lower === "std" || lower === "720p") {
      return "720p — Standard (STD)";
    }
    if (lower === "pro" || lower === "1080p") {
      return "1080p — Professional (PRO)";
    }
  }

  // 2. Resoluciones de vídeo estándar en otros modelos (Wan, Veo, PixVerse, etc.)
  if (lower === "720p") return "720p — Standard";
  if (lower === "1080p") return "1080p — Professional";
  if (lower === "480p") return "480p — Definición estándar";
  if (lower === "540p") return "540p — Media resolución";
  if (lower === "360p") return "360p — Baja resolución";

  // 3. Modelos de imagen con escala K (Nano Banana, Seedream, Qwen, etc.)
  if (lower === "0.5k") return "0.5K — Resolución reducida";
  if (lower === "1k") return "1K — Resolución estándar";
  if (lower === "1.5k") return "1.5K — Resolución media";
  if (lower === "2k") return "2K — Alta resolución";
  if (lower === "3k") return "3K — Muy alta resolución";
  if (lower === "4k") return "4K — Ultraalta resolución";

  // 4. Modelos con megapíxeles (FLUX 2 Flex / Pro / Max)
  if (lower === "1mp") return "1MP — Estándar (~1K)";
  if (lower === "2mp") return "2MP — Alta resolución (~1.4K)";
  if (lower === "3mp") return "3MP — Muy alta resolución (~1.7K)";
  if (lower === "4mp") return "4MP — Ultraalta resolución (~2K)";

  // 5. Modalidades especiales
  if (lower === "quality") return "Calidad alta (Quality)";
  if (lower === "fast") return "Modo rápido (Fast)";
  if (lower === "std") return "720p — Standard (STD)";
  if (lower === "pro") return "1080p — Professional (PRO)";

  return raw;
}

/**
 * Devuelve un resumen corto para el preflight y panel de detalles
 */
export function getResolutionSummary(res, model) {
  if (!res) return "No disponible";
  return formatResolutionLabel(res, model);
}
